
import { 
  collection, 
  addDoc, 
  getDocs, 
  query, 
  where, 
  updateDoc, 
  doc, 
  deleteDoc,
  serverTimestamp,
  writeBatch,
  orderBy
} from "firebase/firestore";
import { db } from "./firebase";
import { PetProfile, HealthEntry, Reminder } from "../types";

export const storage = {
  // Récupérer tous les animaux d'un utilisateur
  getPets: async (userId: string): Promise<PetProfile[]> => {
    try {
      const q = query(collection(db, "pets"), where("userId", "==", userId));
      const querySnapshot = await getDocs(q);
      const pets = querySnapshot.docs.map(doc => {
        const data = doc.data();
        return { 
          id: doc.id, 
          ...data,
          weight: Number(data.weight) 
        } as PetProfile;
      });
      return pets;
    } catch (e) {
      console.error("Error getting pets:", e);
      return [];
    }
  },

  savePet: async (pet: Partial<PetProfile> & { userId: string }) => {
    try {
      if (!pet.userId || typeof pet.userId !== 'string') {
        throw new Error('missing_user_id');
      }

      if (pet.id) {
        const petRef = doc(db, "pets", pet.id);
        const { id, ...data } = pet;
        await updateDoc(petRef, { ...data, updatedAt: serverTimestamp() });
        return pet.id;
      } else {
        // On exclut explicitement 'id' car il peut être undefined venant de l'UI
        const { id, ...data } = pet;
        const docRef = await addDoc(collection(db, "pets"), {
          ...data,
          createdAt: serverTimestamp(),
          updatedAt: serverTimestamp()
        });
        return docRef.id;
      }
    } catch (e) {
      console.error("Error saving pet:", e);
      throw e;
    }
  },

  deletePet: async (petId: string) => {
    try {
      // Supprimer d'abord tous les logs et rappels associés à l'animal
      const logsQuery = query(collection(db, "logs"), where("petId", "==", petId));
      const logsSnapshot = await getDocs(logsQuery);
      
      const remindersQuery = query(collection(db, "reminders"), where("petId", "==", petId));
      const remindersSnapshot = await getDocs(remindersQuery);
      
      // Utiliser un batch pour supprimer tout en une seule transaction
      const batch = writeBatch(db);
      
      // Supprimer les logs
      logsSnapshot.docs.forEach(doc => {
        batch.delete(doc.ref);
      });
      
      // Supprimer les rappels
      remindersSnapshot.docs.forEach(doc => {
        batch.delete(doc.ref);
      });
      
      // Supprimer l'animal
      const petRef = doc(db, "pets", petId);
      batch.delete(petRef);
      
      // Exécuter le batch
      await batch.commit();
    } catch (e) {
      console.error("Error deleting pet:", e);
      throw e;
    }
  },

  getLogs: async (petId: string): Promise<HealthEntry[]> => {
    try {
      const q = query(collection(db, "logs"), where("petId", "==", petId));
      const querySnapshot = await getDocs(q);
      const logs = querySnapshot.docs.map(doc => {
        const data = doc.data();
        return { 
          id: doc.id, 
          ...data,
          weight: (data.weight !== undefined && data.weight !== null && data.weight !== '') ? Number(data.weight) : null,
          stoolScore: data.stoolScore ? Number(data.stoolScore) : null,
          stoolColor: data.stoolColor || null
        } as HealthEntry;
      });
      
      return logs.sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());
    } catch (e) {
      console.error("Error getting logs:", e);
      return [];
    }
  },

  saveLog: async (log: Omit<HealthEntry, 'id'>) => {
    try {
      const docRef = await addDoc(collection(db, "logs"), {
        ...log,
        weight: (log.weight !== undefined && log.weight !== null) ? Number(log.weight) : null,
        stoolScore: log.stoolScore ? Number(log.stoolScore) : null,
        stoolColor: log.stoolColor || null,
        createdAt: serverTimestamp()
      });
      return docRef.id;
    } catch (e) {
      console.error("Error saving log:", e);
      throw e;
    }
  },

  deleteAllLogs: async (petId: string) => {
    try {
      const q = query(collection(db, "logs"), where("petId", "==", petId));
      const querySnapshot = await getDocs(q);
      
      const batch = writeBatch(db);
      querySnapshot.forEach((doc) => {
        batch.delete(doc.ref);
      });
      
      await batch.commit();
      console.log(`${querySnapshot.size} logs deleted.`);
    } catch (e) {
      console.error("Error deleting all logs:", e);
      throw e;
    }
  },

  getReminders: async (petId: string): Promise<Reminder[]> => {
    try {
      const q = query(collection(db, "reminders"), where("petId", "==", petId));
      const querySnapshot = await getDocs(q);
      const reminders = querySnapshot.docs.map(doc => ({ 
        id: doc.id, 
        ...doc.data() 
      } as Reminder));
      
      return reminders
        .filter(r => !r.completed)
        .sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime());
    } catch (e) {
      console.error("Error getting reminders:", e);
      return [];
    }
  },

  saveReminder: async (reminder: Omit<Reminder, 'id'>) => {
    try {
      const docRef = await addDoc(collection(db, "reminders"), {
        ...reminder,
        createdAt: serverTimestamp()
      });
      return docRef.id;
    } catch (e) {
      console.error("Error saving reminder:", e);
      throw e;
    }
  },

  deleteReminder: async (reminderId: string) => {
    try {
      await deleteDoc(doc(db, "reminders", reminderId));
    } catch (e) {
      console.error("Error deleting reminder:", e);
      throw e;
    }
  },

  deleteLog: async (logId: string) => {
    try {
      await deleteDoc(doc(db, "logs", logId));
    } catch (e) {
      console.error("Error deleting log:", e);
      throw e;
    }
  }
};
